import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Stethoscope, Users, FileSearch, Bell, LogOut, Menu, X, Mail, Package, UserCog, CalendarDays, ClipboardList, HeartPulse, Landmark, Syringe, Ticket, FlaskConical, ClipboardCheck, FileText, Pill, Library } from "lucide-react";
import { useEffect, useState, useCallback } from "react";
import { useUnreadMessages } from "@/hooks/useUnreadMessages";

const navItems = [
  { to: "/admin", label: "Visão geral", icon: Stethoscope, end: true },
  { to: "/admin/pacientes", label: "Pacientes", icon: Users, badgeKey: "chat" },
  { to: "/admin/agendamentos", label: "Agendamentos", icon: CalendarDays, badgeKey: "appointments" },
  { to: "/admin/protocolos", label: "Protocolos", icon: ClipboardList },
  { to: "/admin/check-ins", label: "Check-ins", icon: HeartPulse },
  { to: "/admin/servicos", label: "Serviços", icon: Package },
  { to: "/admin/profissionais", label: "Profissionais", icon: UserCog },
  { to: "/admin/aplicacoes", label: "Aplicações", icon: Syringe },
  { to: "/admin/cupons", label: "Cupons", icon: Ticket },
  { to: "/admin/exames", label: "Exames", icon: FileSearch },
  { to: "/admin/marcadores", label: "Marcadores", icon: FlaskConical, group: "Biblioteca" },
  { to: "/admin/farmacos", label: "Fármacos", icon: Pill, group: "Biblioteca" },
  { to: "/admin/padroes", label: "Padrões clínicos", icon: ClipboardCheck, group: "Biblioteca" },
  { to: "/admin/testes-metabolicos", label: "Respostas de testes", icon: FileText, group: "Biblioteca" },
  { to: "/admin/biblioteca-testes", label: "Biblioteca de Testes", icon: Library, group: "Biblioteca" },
  { to: "/admin/anamnese", label: "Ficha de Anamnese", icon: ClipboardCheck, group: "Biblioteca" },
  { to: "/admin/pix", label: "PIX", icon: Landmark, badgeKey: "pix" },
  { to: "/admin/alertas", label: "Alertas", icon: Bell },
  { to: "/admin/emails", label: "Emails", icon: Mail },
];

export default function AdminShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unseenAppts, setUnseenAppts] = useState(0);
  const [pendingPix, setPendingPix] = useState(0);
  const { total: unreadChat, perPatient: unreadPerPatient } = useUnreadMessages(!!user);

  const refreshBadge = useCallback(async () => {
    try {
      const [a, p] = await Promise.all([
        api.get("/admin/appointments/unseen-count"),
        api.get("/admin/pix", { params: { status: "pending" } }).catch(() => ({ data: [] })),
      ]);
      setUnseenAppts(a.data?.count || 0);
      setPendingPix((p.data || []).length);
    } catch (_) { /* noop */ }
  }, []);

  useEffect(() => {
    refreshBadge();
    const t = setInterval(refreshBadge, 20000);
    const handler = () => refreshBadge();
    window.addEventListener("admin-appointments-seen", handler);
    return () => { clearInterval(t); window.removeEventListener("admin-appointments-seen", handler); };
  }, [refreshBadge]);

  const onLogout = async () => { await logout(); navigate("/"); };

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      <aside
        className={`fixed md:sticky top-0 left-0 h-screen w-64 border-r border-border bg-primary text-primary-foreground z-40 transform ${open ? "translate-x-0" : "-translate-x-full"} md:translate-x-0 transition-transform duration-200`}
        data-testid="admin-sidebar"
      >
        <div className="h-16 flex items-center gap-2 px-6 border-b border-primary-foreground/10">
          <Link to="/admin" className="flex items-center gap-2" data-testid="admin-brand-link">
            <div className="w-8 h-8 rounded-sm bg-primary-foreground text-primary grid place-items-center">
              <Stethoscope className="w-4 h-4" />
            </div>
            <div className="leading-tight">
              <div className="brand-wordmark font-bold text-[12px] uppercase">Performance</div>
              <div className="text-[9px] text-gold overline">Painel clínico</div>
            </div>
          </Link>
        </div>
        <nav className="p-3 space-y-1">
          {navItems.map(({ to, label, icon: Icon, end, badgeKey }) => (
            <NavLink
              key={to} to={to} end={end}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-sm text-sm font-medium transition-colors ${
                  isActive ? "bg-primary-foreground text-primary" : "text-primary-foreground hover:bg-primary-foreground/10"
                }`
              }
              data-testid={`admin-nav-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}
            >
              <Icon className="w-4 h-4" />
              <span className="flex-1">{label}</span>
              {badgeKey === "appointments" && unseenAppts > 0 && (
                <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-gold text-primary text-[10px] font-bold grid place-items-center" data-testid="badge-unseen-appointments">
                  {unseenAppts > 9 ? "9+" : unseenAppts}
                </span>
              )}
              {badgeKey === "pix" && pendingPix > 0 && (
                <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-gold text-primary text-[10px] font-bold grid place-items-center" data-testid="badge-pending-pix">
                  {pendingPix > 9 ? "9+" : pendingPix}
                </span>
              )}
              {badgeKey === "chat" && unreadChat > 0 && (
                <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold grid place-items-center" data-testid="admin-chat-unread-badge" title={`${unreadChat} mensagem(ns) do chat não lida(s)`}>
                  {unreadChat > 99 ? "99+" : unreadChat}
                </span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-primary-foreground/10">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-9 h-9 rounded-full bg-primary-foreground/20 grid place-items-center font-semibold">
              {(user?.name || "A").charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{user?.name}</div>
              <div className="text-xs opacity-70 truncate">{user?.email}</div>
            </div>
            <button
              onClick={onLogout}
              className="p-2 rounded-sm hover:bg-primary-foreground/10 transition-colors"
              data-testid="admin-logout-btn"
              title="Sair"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {open && <div className="fixed inset-0 bg-black/40 z-30 md:hidden" onClick={() => setOpen(false)} />}

      <div className="flex-1 min-w-0">
        <header className="sticky top-0 z-20 bg-white/80 backdrop-blur-xl border-b border-black/5">
          <div className="h-16 flex items-center justify-between px-4 md:px-8">
            <button
              className="md:hidden p-2 rounded-sm hover:bg-muted"
              onClick={() => setOpen(!open)}
              data-testid="admin-sidebar-toggle"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <div className="overline text-muted-foreground hidden md:block">Painel clínico — farmacêutico</div>
            <div className="flex items-center gap-3">
              {unseenAppts > 0 && (
                <Link to="/admin/agendamentos" className="inline-flex items-center gap-1 rounded-sm bg-gold/20 text-gold px-2.5 py-1 text-xs font-semibold hover:bg-gold/30 transition-colors" data-testid="header-unseen-appointments">
                  <CalendarDays className="w-3 h-3" /> {unseenAppts} novo{unseenAppts > 1 ? "s" : ""}
                </Link>
              )}
              <div className="text-sm text-muted-foreground">{user?.name}</div>
            </div>
          </div>
        </header>
        <main className="p-4 md:p-8">{children}</main>
      </div>
    </div>
  );
}
